import React from "react";
import {
	Box,
	Divider,
	Drawer as MuiDrawer,
	List,
	ListItem,
	ListItemAvatar,
	Avatar,
	ListItemText,
	Typography,
	Button,
	IconButton,
	Tooltip,
} from "@mui/material";
import { styled, Theme, CSSObject, useTheme } from "@mui/material/styles";
import { Navigation } from "@/types/navigation";
import { NavigationItem } from "./NavigationItem";

import { DRAWER_WIDTH, DRAWER_WIDTH_COLLAPSED, HEADER_HEIGHT } from "./config";
/** ICONS */
import CreditCardIcon from "@mui/icons-material/CreditCard";
import ZapIcon from "@mui/icons-material/FlashOn";

import { SidebarFooter } from "./SidebarFooter";

const openedMixin = (theme: Theme): CSSObject => ({
	width: DRAWER_WIDTH,
	transition: theme.transitions.create("width", {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.enteringScreen,
	}),
	overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
	transition: theme.transitions.create("width", {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.leavingScreen,
	}),
	overflowX: "hidden",
	width: `${DRAWER_WIDTH_COLLAPSED}px`,
});

const StyledDrawer = styled(MuiDrawer, {
	shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
	width: DRAWER_WIDTH,
	flexShrink: 0,
	whiteSpace: "nowrap",
	boxSizing: "border-box",
	// --- STYLE CORRECTIONS APPLIED HERE ---
	"& .MuiDrawer-paper": {
		backgroundColor: theme.palette.background.secondary,
		scrollbarWidth: "thin",
		scrollbarColor: `${theme.palette.mode === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)"} transparent`,
		"&::-webkit-scrollbar": {
			width: "6px",
		},
		"&::-webkit-scrollbar-track": {
			background: "transparent",
		},
		"&::-webkit-scrollbar-thumb": {
			backgroundColor:
				theme.palette.mode === "dark"
					? "rgba(255,255,255,0.2)"
					: "rgba(0,0,0,0.2)",
			borderRadius: "10px",
		},
		"&::-webkit-scrollbar-thumb:hover": {
			backgroundColor:
				theme.palette.mode === "dark"
					? "rgba(255,255,255,0.3)"
					: "rgba(0,0,0,0.3)",
		},
	},
	// --- END OF CORRECTIONS ---
	...(open && {
		...openedMixin(theme),
		"& .MuiDrawer-paper": {
			...openedMixin(theme),
			// We also need to re-apply the background color for the open state
			backgroundColor: theme.palette.background.secondary,
		},
	}),
	...(!open && {
		...closedMixin(theme),
		"& .MuiDrawer-paper": {
			...closedMixin(theme),
			// And re-apply it for the closed state
			backgroundColor: theme.palette.background.secondary,
		},
	}),
}));

interface NavigationDrawerProps {
	open: boolean;
	headerHeight: number;
	navigations: Navigation[];
	activePath: string;
}

export const NavigationDrawer: React.FC<NavigationDrawerProps> = ({
	open,
	headerHeight,
	navigations,
}) => {
	const theme = useTheme();

	return (
		<StyledDrawer variant="permanent" open={open}>
			<Box
				sx={{
					height: HEADER_HEIGHT,
					p: 2,
					display: "flex",
					alignItems: "center",
				}}
			>
				<ListItem sx={{ p: 0 }}>
					<ListItemAvatar>
						<Avatar
							src={`${process.env.PUBLIC_URL}/homemedia.jpg`}
							sx={{ width: 30, height: 30 }}
						/>
					</ListItemAvatar>
					{open && (
						<ListItemText primary="ЭН БИ СИ" secondary="Төлбөрийн систем" />
					)}
				</ListItem>
			</Box>
			<Divider />
			<Box
				sx={{
					flexGrow: 1,
					overflowY: "auto",
					overflowX: "hidden",
					scrollbarWidth: "thin",
					scrollbarColor: `${theme.palette.mode === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)"} transparent`,
					"&::-webkit-scrollbar": {
						width: "6px",
					},
					"&::-webkit-scrollbar-track": {
						background: "transparent",
					},
					"&::-webkit-scrollbar-thumb": {
						backgroundColor:
							theme.palette.mode === "dark"
								? "rgba(255,255,255,0.2)"
								: "rgba(0,0,0,0.2)",
						borderRadius: "10px",
					},
					"&::-webkit-scrollbar-thumb:hover": {
						backgroundColor:
							theme.palette.mode === "dark"
								? "rgba(255,255,255,0.3)"
								: "rgba(0,0,0,0.3)",
					},
				}}
			>
				<List sx={{ mt: 2 }} component="nav">
					{navigations.map((item) => (
						<NavigationItem key={item.id} item={item} open={open} />
					))}
				</List>
			</Box>
			<SidebarFooter open={open} />
		</StyledDrawer>
	);
};
